import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { config } from '../config';
import { buildGoogleAuthorizeUrl, exchangeCodeForToken } from './cognito';
import * as srp from './cognitoSrp';
import { clearIdToken, getIdToken, setIdToken, setRefreshToken } from './token';

interface AuthContextValue {
  /** True once we have a usable token (mock or real) and initial checks are done. */
  isAuthenticated: boolean;
  /** True while we're still figuring out auth state (e.g. restoring a session, exchanging a callback code). */
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Redirects straight to Google's own sign-in screen (no Hosted UI page in between). */
  googleSignIn: () => void;
  logout: () => void;
  /** Called by the /auth/callback route with the `code` query param (the Google return trip). */
  completeLogin: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const MOCK_ID_TOKEN = 'mock.jwt.token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (config.useMock) {
      // Mock mode: skip real auth entirely, auto-log-in as a fake local user so the whole UI is
      // clickable without any backend.
      setIdToken(MOCK_ID_TOKEN);
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    (async () => {
      // A stored token from a prior session (or the Google callback) short-circuits restoration;
      // otherwise try to restore from Cognito's own locally-cached refresh token.
      const stored = getIdToken();
      if (stored) {
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }
      const restored = await srp.restoreSession();
      if (restored) {
        setIdToken(restored);
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    })();
  }, []);

  const signIn = async (email: string, password: string) => {
    if (config.useMock) {
      setIdToken(MOCK_ID_TOKEN);
      setIsAuthenticated(true);
      return;
    }
    const idToken = await srp.signIn(email, password);
    setIdToken(idToken);
    setIsAuthenticated(true);
  };

  const signUp = async (email: string, password: string) => {
    if (config.useMock) return;
    await srp.signUp(email, password);
  };

  const confirmSignUp = async (email: string, code: string) => {
    if (config.useMock) return;
    await srp.confirmSignUp(email, code);
  };

  const resendCode = async (email: string) => {
    if (config.useMock) return;
    await srp.resendConfirmationCode(email);
  };

  const forgotPassword = async (email: string) => {
    if (config.useMock) return;
    await srp.forgotPassword(email);
  };

  const confirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    if (config.useMock) return;
    await srp.confirmForgotPassword(email, code, newPassword);
  };

  const googleSignIn = () => {
    if (config.useMock) {
      setIdToken(MOCK_ID_TOKEN);
      setIsAuthenticated(true);
      return;
    }
    window.location.assign(buildGoogleAuthorizeUrl());
  };

  const logout = () => {
    clearIdToken();
    if (!config.useMock) srp.signOutLocal();
    setIsAuthenticated(false);
  };

  const completeLogin = async (code: string) => {
    setIsLoading(true);
    try {
      const { idToken, refreshToken } = await exchangeCodeForToken(code);
      setIdToken(idToken);
      if (refreshToken) setRefreshToken(refreshToken);
      setIsAuthenticated(true);
    } finally {
      setIsLoading(false);
    }
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      signIn,
      signUp,
      confirmSignUp,
      resendCode,
      forgotPassword,
      confirmForgotPassword,
      googleSignIn,
      logout,
      completeLogin,
    }),
    [isAuthenticated, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
